import { SuiteProductAccess } from '@/components/suite/SuiteProductAccess';
import { SignOutButton } from "../components/common/SignOutButton";
import { centralEnabled } from '@/config/api'
import { accountsPortal } from '@/services/centralAuth'
import { useEffect, useState } from 'react'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { crmReturnPath } from '@/helpers/authNavigation'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faBuilding, faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { useAuth } from '@/context/AuthContext'
import { roleLabel } from '@/helpers/roles'
import type { BusinessOption } from '@/models/auth'
import { readError } from '@/services/apiClient'
import { loadBusinesses } from '@/services/authService'

export function SelectBusinessPage() {
  const { session, ready, selectBusiness, signOut, accessError, retryAccess } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const returnTo = crmReturnPath(location.state?.returnTo)
  const [businesses, setBusinesses] = useState<BusinessOption[]>(session?.businesses ?? [])
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const selected = businesses.find((business) => business.businessUserId === selectedId)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!session || businesses.length > 0) {
      return
    }
    let cancelled = false
    setLoading(true)
    loadBusinesses()
      .then((rows) => {
        if (!cancelled) setBusinesses(rows)
      })
      .catch((caught) => {
        if (!cancelled) setError(readError(caught))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [businesses.length, session])

  if (accessError) return <Alert variant="warning">Unable to verify CRM access right now. <Button onClick={retryAccess}>Retry</Button></Alert>;

  if (!ready) {
    return null
  }
  if (!session) {
    return <Navigate to="/signin" replace />
  }
  if (!centralEnabled && session.purpose === 'access' && !session.requiresBusinessSelection) {
    return <Navigate to="/" replace />
  }

  async function choose(businessUserId: string) {
    setBusyId(businessUserId)
    setError(null)
    try {
      await selectBusiness(businessUserId)
      if (centralEnabled) navigate(returnTo, { replace: true })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that business')
      setBusyId(null)
    }
  }

  if (centralEnabled && !loading && !error && businesses.length === 0) return <SuiteProductAccess product="crm" />;

  return (
    <AuthLayout
      variant="selection"
      badge="Business selection"
      title="Select your workspace"
      description="Choose one business to continue into CRM."
    >
      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Spinner animation="border" size="sm" role="status">
            <span className="visually-hidden">Loading businesses</span>
          </Spinner>
          <span>Loading your businesses</span>
        </div>
      )}
      {!loading && !error && businesses.length === 0 && (
        <Alert variant="secondary">No businesses are available for this account. {centralEnabled && <a href={`${accountsPortal}/crm`}>Review CRM access in Accounts</a>}</Alert>
      )}
      {businesses.length > 0 && (
        <>
          <div className="business-select-list" aria-label="Available businesses">
            {businesses.map((business) => (
              <button key={business.businessUserId} type="button"
                className={`business-select-card${selectedId === business.businessUserId ? ' business-select-card--active' : ''}`}
                aria-pressed={selectedId === business.businessUserId}
                disabled={busyId != null} onClick={() => setSelectedId(business.businessUserId)}>
                <div className="business-select-card__main">
                  <span className="business-select-card__icon"><FontAwesomeIcon icon={faBuilding} /></span>
                  <div className="business-select-card__content">
                    <div className="business-select-card__title-row">
                      <span className="business-select-card__title">{business.businessName}</span>
                      {selectedId === business.businessUserId && <span className="business-select-card__status"><FontAwesomeIcon icon={faCircleCheck} /> Selected</span>}
                    </div>
                    <span className="business-select-card__meta">{roleLabel(business.role)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="signin-form__actions signin-form__actions--split">
            <p className="signin-form__text mb-0" aria-live="polite">{selected ? `${selected.businessName} will open first.` : 'Select a business to continue.'}</p>
            <Button size="lg" disabled={!selected || busyId != null} onClick={() => selected && void choose(selected.businessUserId)}>
              {busyId ? 'Opening…' : 'Continue'} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </>
      )}
      <SignOutButton appName="CRM" variant="link" className="px-0" onConfirm={signOut}>Use a different account</SignOutButton>
    </AuthLayout>
  )
}
